import React, { useEffect, useRef, useState } from "react";
import { Icon } from "~/components/ui";
import { cn } from "~/lib/utils";

import ActionToolTip from "./action-tooltip";

interface Props {
  actionMode: boolean;
  name: string;
  cancelAction: () => void;
  saveUser: (name: string) => void;
  deleteUser: () => void;
}

const AddUserNode = ({
  name,
  actionMode,
  saveUser,
  cancelAction,
  deleteUser,
}: Props) => {
  const [isInputError, setIsInputError] = useState(false);
  const [editedName, setEditedName] = useState(name);

  const inputRef = useRef<HTMLInputElement>(null);

  // Helper function to dynamically set the input width
  const adjustInputWidth = () => {
    if (!inputRef.current) return;

    const input = inputRef.current;
    const currentValue = input.value.trim();

    if (!currentValue) {
      // If empty, default to 40px
      input.style.width = "40px";
    } else {
      // Temporarily reset width so scrollWidth is calculated correctly
      input.style.width = "0px";
      const newWidth = Math.max(40, input.scrollWidth + 2);
      input.style.width = `${newWidth}px`;
    }
  };

  // On initial mount or if `name` changes, sync the input value & adjust width
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = name;
      adjustInputWidth();
    }
    setEditedName(name);
  }, [name]);

  // If `editedName` changes externally, also adjust the width
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = editedName;
    }
    adjustInputWidth();
  }, [editedName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setEditedName(val);
    setIsInputError(!val.trim());
    adjustInputWidth();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (editedName.trim()) {
        // If not empty, blur & save
        e.currentTarget.blur();
        saveUser(editedName);
      }
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.currentTarget.blur();
      cancelAction();
    }
  };

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2 rounded-3xl border border-neutral-300 bg-neutral-200 px-2.5 py-0.5",
        !isInputError && actionMode && "border-2 border-secondary-500",
        isInputError && "border-2 border-danger-500",
        { "bg-white": actionMode },
      )}
    >
      {actionMode ? (
        <input
          ref={inputRef}
          type="text"
          className="text-sm underline focus-visible:outline-none"
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <span className="max-w-[8ch] overflow-hidden text-ellipsis whitespace-nowrap text-sm">
          {name}
        </span>
      )}

      {/* Delete button only when not in action mode */}
      {!actionMode && (
        <ActionToolTip title="Delete">
          <Icon
            name="X"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              deleteUser();
            }}
            className="cursor-pointer text-neutral-400"
            size={20}
          />
        </ActionToolTip>
      )}

      {/* Ok/Cancel when in action mode */}
      {actionMode && (
        <div className="flex items-center justify-center gap-0.5 [&>svg]:cursor-pointer">
          <ActionToolTip title="Ok">
            <Icon
              name="Check"
              onClick={(event) => {
                if (!isInputError) {
                  event.preventDefault();
                  event.stopPropagation();
                  saveUser(editedName || name);
                }
              }}
              className="text-success-500"
              size={20}
            />
          </ActionToolTip>
          <ActionToolTip title="Cancel">
            <Icon
              name="X"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                cancelAction();
              }}
              className="cursor-pointer text-neutral-400"
              size={20}
            />
          </ActionToolTip>
        </div>
      )}
    </div>
  );
};

export default AddUserNode;
